import React, { useState } from "react";
import { toast } from "react-toastify";

export interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  className?: string;
  valueClassName?: string;
}

export const InfoRow: React.FC<InfoRowProps> = ({ label, value, className = "", valueClassName = "" }) => {
  return (
    <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 last:border-b-0 ${className}`}>
      <span className="text-sm text-gray-600 font-medium mb-1 sm:mb-0">{label}</span>
      <span className={`text-base text-gray-900 font-semibold break-words text-right sm:text-left ${valueClassName}`}>{value}</span>
    </div>
  );
};

// ✅ Editable row with zero-clear + max validation + negative prevention + toast
export const EditableInfoRow: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  max: number;
  className?: string;
  valueClassName?: string;
}> = ({ label, value, onChange, max, className = "", valueClassName = "" }) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (value === 0) {
      e.target.value = ""; // ✅ Clear the zero when focusing
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    const num = parseFloat(e.target.value) || 0;

    // Ensure non-negative value on blur
    if (num < 0) {
      toast.error("Received amount cannot be negative", {
        position: "top-center",
        autoClose: 2500,
      });
      onChange(0);
    } else {
      onChange(num);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let num = parseFloat(e.target.value);

    if (isNaN(num)) {
      num = 0;
    }

    // Prevent negative values
    if (num < 0) {
      toast.error("Received amount cannot be negative", {
        position: "top-center",
        autoClose: 2500,
      });
      onChange(0);
      return;
    }

    // Prevent values exceeding max
    if (num > max) {
      toast.error(`Received amount cannot exceed total price (₹${max.toFixed(2)})`, {
        position: "top-center",
        autoClose: 2500,
      });
      onChange(max);
    } else {
      onChange(num);
    }
  };

  // Handle key press to prevent negative input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-") {
      e.preventDefault();
    }
  };

  return (
    <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 last:border-b-0 ${className}`}>
      <span className="text-sm text-gray-600 font-medium mb-1 sm:mb-0">{label}</span>
      <input
        type="number"
        value={isFocused && value === 0 ? "" : value} // ✅ Shows blank on focus if 0
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        min="0" // HTML5 attribute to prevent negative values in number input
        max={max}
        className={`w-full sm:w-36 px-3 py-1.5 border border-gray-300 rounded-lg text-right font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${valueClassName}`}
      />
    </div>
  );
};
